import * as React from "react";
import { Card, CardBody, PageContainer } from "../components/Card";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/Button";
import { Select, Input } from "../components/Form";
import { Banner, ListStates } from "../components/States";
import { Toolbar, FilterPill } from "../components/Toolbar";
import { useAsync } from "../general/useAsync";
import { ModulePicker } from "../activities/ModulePicker";
import { FindingList, FindingSummary } from "./FindingList";
import * as Store from "./ProceduresStore";

/**
 * The engine's validation output, browsable.
 *
 * Findings are computed on demand and never persisted, so this is always a live answer to "would a
 * run work right now" rather than a log of past problems. That is also why there is nothing to
 * acknowledge or dismiss - the way to make a finding go away is to fix what it points at.
 */

const SEVERITIES = ["Error", "Warning", "Information"] as const;

export const FindingsIndex: React.FC = () => {
    const [severity, setSeverity] = React.useState("");
    const [moduleID, setModuleID] = React.useState("");
    const [code, setCode] = React.useState("");
    const [studentID, setStudentID] = React.useState("");
    const [page, setPage] = React.useState(1);

    const pageSize = 50;

    const findings = useAsync(
        () =>
            Store.listFindings({
                severity: severity || undefined,
                code: code.trim() || undefined,
                qualificationModuleID: moduleID || undefined,
                studentID: studentID.trim() || undefined,
                page,
                pageSize,
            }),
        [severity, code, moduleID, studentID, page],
        // Typing a code or a student id should not fire a request per keystroke.
        { debounceMs: 300 }
    );

    // Any filter change invalidates the current page number.
    React.useEffect(() => setPage(1), [severity, code, moduleID, studentID]);

    const data = findings.data;
    const rows = data?.findings ?? [];
    const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / (data?.pageSize ?? pageSize)));

    return (
        <PageContainer>
            <PageHeader
                title="Findings"
                icon={["fad", "triangle-exclamation"]}
                description="Calculation coverage, warnings and errors from the engine, computed live."
                actions={<FindingSummary findings={rows} />}
            />

            <Card className="mb-4">
                <CardBody className="p-4">
                    <div className="mb-3 grid gap-3 lg:grid-cols-2">
                        <ModulePicker value={moduleID} onChange={setModuleID} label="Qualification module" />

                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">Code</label>
                            <Input
                                value={code}
                                placeholder="e.g. DuplicateOutputOwner"
                                onChange={(e) => setCode(e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">Student id</label>
                            <Input value={studentID} placeholder="Identity-server user id" onChange={(e) => setStudentID(e.target.value)} />
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">Severity</label>
                            <Select value={severity} placeholder="Any severity" onChange={(e) => setSeverity(e.target.value)}>
                                {SEVERITIES.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    <Toolbar className="mb-0">
                        {SEVERITIES.map((s) => (
                            <FilterPill key={s} label={s} active={severity === s} onToggle={() => setSeverity(severity === s ? "" : s)} />
                        ))}
                        {(severity || code || moduleID || studentID) && (
                            <Button
                                size="sm"
                                variant="ghost"
                                icon={["fad", "xmark"]}
                                onClick={() => {
                                    setSeverity("");
                                    setCode("");
                                    setModuleID("");
                                    setStudentID("");
                                }}
                            >
                                Clear filters
                            </Button>
                        )}
                    </Toolbar>
                </CardBody>
            </Card>

            <Card>
                <CardBody>
                    <ListStates
                        isLoading={findings.isLoading}
                        error={findings.error}
                        onRetry={findings.refresh}
                        isEmpty={rows.length === 0}
                        empty={{
                            icon: ["fad", "circle-check"],
                            title: "No findings",
                            hint:
                                severity || code || moduleID || studentID
                                    ? "Nothing matches these filters."
                                    : "Everything the engine can see right now validates cleanly.",
                        }}
                    >
                        <FindingList findings={rows} />
                    </ListStates>

                    {totalPages > 1 && (
                        <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                            <span className="text-xs text-text-weak">
                                Page {data?.page ?? page} of {totalPages} - {data?.totalCount ?? 0} finding(s)
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    icon={["fad", "chevron-left"]}
                                    disabled={page <= 1 || findings.isLoading}
                                    onClick={() => setPage(page - 1)}
                                >
                                    Previous
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    icon={["fad", "chevron-right"]}
                                    disabled={page >= totalPages || findings.isLoading}
                                    onClick={() => setPage(page + 1)}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>

            <Banner tone="info" className="mt-4">
                Findings are worked out on demand and never stored. There is nothing to dismiss - a finding disappears once the thing it
                points at is fixed.
            </Banner>
        </PageContainer>
    );
};

export default FindingsIndex;
